/**
 * ExportMenu — dropdown with all export actions.
 * Includes resolution picker (1×/2×/3×) for PNG and PDF exports,
 * plus a separate Outlook-safe HTML export option.
 */
import React, { useState, useRef } from 'react';
import { Download, FileText, Mail, Image, Package, Copy, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useEditorStore } from '../stores/editorStore';
import {
  exportWebHtml, exportEmailHtml, exportOutlookHtml,
  captureA4Image, captureA4Pdf,
  exportZipBundle, copyHtmlToClipboard,
} from '../lib/exportUtils';

// Resolution labels / pixel ratios
const RESOLUTIONS = [
  { label: '1×', value: 1 },
  { label: '2×', value: 2 },
  { label: '3×', value: 3 },
];

export function ExportMenu({ canvasRef }) {
  const [open, setOpen]         = useState(false);
  const [resolution, setResolution] = useState(2); // default 2×
  const { title, sections, meta } = useEditorStore();
  const btnRef = useRef(null);

  const run = async (fn, label) => {
    setOpen(false);
    try {
      await fn();
      toast.success(`${label} done`);
    } catch (err) {
      toast.error(`${label} failed: ${err.message}`);
    }
  };

  const actions = [
    {
      label: 'Web HTML',
      Icon: FileText,
      fn: () => exportWebHtml(title, sections, meta),
      group: 'html',
    },
    {
      label: 'Email HTML',
      Icon: Mail,
      fn: () => exportEmailHtml(title, sections, meta),
      group: 'html',
    },
    {
      label: 'Outlook HTML',
      Icon: Mail,
      fn: () => exportOutlookHtml(title, sections, meta),
      group: 'html',
      badge: 'Outlook',
    },
    {
      label: 'PNG Preview',
      Icon: Image,
      fn: async () => {
        const dataUrl = await captureA4Image(canvasRef, resolution);
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${title || 'newsletter'}.png`;
        a.click();
      },
      group: 'image',
    },
    {
      label: 'PDF (A4)',
      Icon: FileText,
      fn: () => captureA4Pdf(canvasRef, title, resolution),
      group: 'image',
    },
    {
      label: 'ZIP Bundle',
      Icon: Package,
      fn: () => exportZipBundle(title, sections, meta, canvasRef),
      group: 'other',
    },
    {
      label: 'Copy HTML',
      Icon: Copy,
      fn: () => copyHtmlToClipboard(sections, meta),
      group: 'other',
    },
  ];

  return (
    <div style={{ position: 'relative' }} ref={btnRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#111827', color: '#fff',
          border: 'none', borderRadius: 8, padding: '8px 14px',
          cursor: 'pointer', fontSize: 13, fontWeight: 500,
        }}
      >
        <Download size={14}/> Export <ChevronDown size={12}/>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 99 }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            background: 'var(--em-bg-card)', border: '1px solid var(--em-border)', borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 100,
            minWidth: 210, overflow: 'hidden',
          }}>
            {/* Resolution picker — affects PNG and PDF */}
            <div style={{
              padding: '10px 14px 8px',
              borderBottom: '1px solid #f3f4f6',
            }}>
              <p style={{ margin: '0 0 6px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Image Quality
              </p>
              <div style={{ display: 'flex', gap: 4 }}>
                {RESOLUTIONS.map(({ label, value }) => (
                  <button
                    key={value}
                    onClick={() => setResolution(value)}
                    style={{
                      flex: 1, padding: '4px 0',
                      border: `1px solid ${resolution === value ? '#f54b01' : 'var(--em-border)'}`,
                      borderRadius: 6,
                      background: resolution === value ? '#fff7f5' : '#fff',
                      color: resolution === value ? '#f54b01' : '#6b7280',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p style={{ margin: '5px 0 0', fontSize: 10, color: '#d1d5db' }}>
                Applies to PNG Preview &amp; PDF
              </p>
            </div>

            {/* Action items */}
            {actions.map(({ label, Icon, fn, badge }) => (
              <button
                key={label}
                onClick={() => run(fn, label)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '10px 14px',
                  background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 13, color: '#374151', textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <Icon size={14} color="#f54b01"/>
                <span style={{ flex: 1 }}>{label}</span>
                {badge && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 5px',
                    background: '#fffbeb', color: '#92400e',
                    border: '1px solid #fde68a', borderRadius: 4,
                    textTransform: 'uppercase', letterSpacing: 0.3,
                  }}>
                    {badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
